'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { X, Clock, Save, Loader2, Calendar } from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { IssueCard } from '@/components/issues/issue-card'
import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'

interface Activity {
  id: number
  name: string
}

interface Issue {
  id: number
  number?: number
  subject: string
  description?: string
  tracker?: {
    id: number
    name: string
  }
  status?: {
    id: number
    name: string
    is_closed: boolean
  }
  priority?: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  updated_on?: string
  done_ratio?: number
  spent_hours?: number
  project: {
    id: number
    name: string
  }
}

interface GlobalTimeTrackerProps {
  isOpen: boolean
  onClose: () => void
  issueId?: number
  projectId?: number
  elapsedSeconds?: number
  timeEntryId?: number
  timeEntry?: any
  onSuccess?: () => void
}

export function GlobalTimeTracker({
  isOpen,
  onClose,
  issueId: initialIssueId,
  projectId: initialProjectId,
  elapsedSeconds: initialElapsedSeconds,
  timeEntryId,
  timeEntry,
  onSuccess
}: GlobalTimeTrackerProps) {
  const { activeTimer, getElapsedTime, stopTimer } = useTimeTracking()
  const [activities, setActivities] = useState<Activity[]>([])
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [issueSearch, setIssueSearch] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [defaultActivityId, setDefaultActivityId] = useState<string>('')
  const [isManuallyEditingHours, setIsManuallyEditingHours] = useState(false)

  const isTimerActive = activeTimer?.issueId === initialIssueId

  const [formData, setFormData] = useState({
    hours: '',
    activity_id: defaultActivityId,
    issue_id: initialIssueId?.toString() || '',
    comments: '',
    spent_on: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    if (isOpen) {
      // Reset manual editing flag when modal opens
      setIsManuallyEditingHours(false)
      
      fetchActivities()

      // If we have a specific issue ID, ONLY fetch that issue (not the general list)
            const loadIssues = async () => {
        if (initialIssueId) {
          // Only fetch the specific issue
          await fetchSpecificIssue(initialIssueId)
        } else {
          // No specific issue, fetch general list
          await fetchIssues()
        }
      }

      loadIssues()

      // Reset and pre-fill form based on context
      // Priority 0: Editing existing time entry
      if (timeEntry) {
                setFormData({
          hours: timeEntry.hours.toString(),
          activity_id: timeEntry.activity?.id?.toString() || defaultActivityId,
          issue_id: timeEntry.issue?.id?.toString() || initialIssueId?.toString() || '',
          comments: timeEntry.comments || '',
          spent_on: timeEntry.spent_on || new Date().toISOString().split('T')[0]
        })
      }
      // Priority 1: Elapsed seconds from stopped timer
      else if (initialElapsedSeconds && initialElapsedSeconds > 0) {
        const hours = (initialElapsedSeconds / 3600).toFixed(2)
        setElapsedSeconds(initialElapsedSeconds)
        setFormData({
          hours: hours,
          activity_id: defaultActivityId,
          issue_id: initialIssueId?.toString() || '',
          comments: `Working on #${initialIssueId}`,
          spent_on: new Date().toISOString().split('T')[0]
        })
      }
      // Priority 2: Active timer
      else if (isTimerActive && activeTimer) {
        const elapsed = getElapsedTime()
        setElapsedSeconds(elapsed)
        const hours = (elapsed / 3600).toFixed(2)
        setFormData({
          hours: hours,
          activity_id: defaultActivityId,
          issue_id: initialIssueId?.toString() || '',
          comments: activeTimer.description || `Working on #${initialIssueId}`,
          spent_on: new Date().toISOString().split('T')[0]
        })
      }
      // Priority 3: Just issue ID
      else if (initialIssueId) {
        setFormData({
          hours: '',
          activity_id: defaultActivityId,
          issue_id: initialIssueId.toString(),
          comments: '',
          spent_on: new Date().toISOString().split('T')[0]
        })
      }
      // Priority 4: Empty form
      else {
        setFormData({
          hours: '',
          activity_id: defaultActivityId,
          issue_id: '',
          comments: '',
          spent_on: new Date().toISOString().split('T')[0]
        })
      }
    }
  }, [isOpen, initialIssueId, initialElapsedSeconds, isTimerActive, activeTimer, getElapsedTime, timeEntry, defaultActivityId])

  // Update elapsed time when timer is active - every second
  useEffect(() => {
    if (!isTimerActive || !isOpen || !activeTimer) return

    // Calculate elapsed time locally
    const calculateElapsed = () => {
      if (activeTimer.isPaused) {
        return activeTimer.pausedTime
      }
      const currentElapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
      return activeTimer.pausedTime + currentElapsed
    }

    // Only auto-update hours if timer is running (not paused) AND user hasn't manually edited
    const interval = setInterval(() => {
      const elapsed = calculateElapsed()
      setElapsedSeconds(elapsed)
      
      // Only update hours field if timer is running and user hasn't manually edited it
      if (!activeTimer.isPaused && !isManuallyEditingHours) {
        const hours = (elapsed / 3600).toFixed(2)
        setFormData(prev => ({
          ...prev,
          hours: hours
        }))
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isTimerActive, isOpen, activeTimer, isManuallyEditingHours])

  // Fetch issue details when selected
  useEffect(() => {
    if (formData.issue_id) {
      const issue = issues.find(i => i.id.toString() === formData.issue_id)
      setSelectedIssue(issue || null)
    } else {
      setSelectedIssue(null)
    }
  }, [formData.issue_id, issues])

  const fetchActivities = async () => {
    try {
      setLoading(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        // Fallback to default activities if no auth
        const fallbackActivities = [
          { id: 8, name: 'Development' },
          { id: 9, name: 'Design' }
        ]
        setActivities(fallbackActivities)
        setDefaultActivityId(fallbackActivities[0].id.toString())
        return
      }

      // Fetch time entry activities
      const response = await fetch('/api/enumerations/time_entry_activities', {
        headers: authHeaders
      })

      if (response.ok) {
        const data = await response.json()
        // Handle both wrapped and direct array responses
        const activitiesList = data.time_entry_activities || data || []
        
        if (activitiesList.length > 0) {
          setActivities(activitiesList)
          const firstActivityId = activitiesList[0].id.toString()
          setDefaultActivityId(firstActivityId)
          
          // Update form data if activity_id is not set or if editing time entry
          setFormData(prev => ({
            ...prev,
            activity_id: prev.activity_id || firstActivityId
          }))
        } else {
          // No activities returned, use fallback
          const fallbackActivities = [
            { id: 8, name: 'Development' },
            { id: 9, name: 'Design' }
          ]
          setActivities(fallbackActivities)
          setDefaultActivityId(fallbackActivities[0].id.toString())
        }
      } else {
        // Fallback to default activities if API fails
        const fallbackActivities = [
          { id: 8, name: 'Development' },
          { id: 9, name: 'Design' }
        ]
        setActivities(fallbackActivities)
        setDefaultActivityId(fallbackActivities[0].id.toString())
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error)
      // Fallback to default activities
      const fallbackActivities = [
        { id: 8, name: 'Development' },
        { id: 9, name: 'Design' }
      ]
      setActivities(fallbackActivities)
      setDefaultActivityId(fallbackActivities[0].id.toString())
    } finally {
      setLoading(false)
    }
  }

  const fetchIssues = async (searchTerm: string = '') => {
    try {
      setIsSearching(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      // Fetch all issues with search if provided, otherwise get user's recent issues
      let url = '/api/issues?limit=100&sort=updated_on:desc'
      if (searchTerm) {
        url += `&subject=~${encodeURIComponent(searchTerm)}`
      }

      const response = await fetch(url, {
        headers: authHeaders
      })

      if (response.ok) {
        const data = await response.json()
        const fetchedIssues = data.issues || []

        // Preserve selected issue if it exists and not in new results
        setIssues(prevIssues => {
          if (selectedIssue && !fetchedIssues.some((i: Issue) => i.id === selectedIssue.id)) {
            return [selectedIssue, ...fetchedIssues]
          }
          return fetchedIssues
        })
      }
    } catch (error) {
    } finally {
      setIsSearching(false)
    }
  }

  const fetchSpecificIssue = async (issueId: number) => {
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      const response = await fetch(`/api/issues/${issueId}`, {
        headers: authHeaders
      })

      if (response.ok) {
        const data = await response.json()
        const issue = data.issue

        // Add the specific issue to the issues list if not already there
        setIssues(prevIssues => {
          const exists = prevIssues.some(i => i.id === issue.id)
          if (exists) {
            return prevIssues
          }
          return [issue, ...prevIssues]
        })

        // Set as selected issue
        setSelectedIssue(issue)
      }
    } catch (error) {
      console.error('Error fetching specific issue:', error)
    }
  }

  // Debounced search for issues
  // Only trigger search if:
  // 1. Modal is open
  // 2. User is actively searching (issueSearch has content)
  // 3. No initial issue was provided (if initialIssueId exists, we don't want to override it)
  useEffect(() => {
    if (!isOpen) return

    // Don't trigger search on initial load if we have a specific issue
    if (initialIssueId && !issueSearch) return

    // Only search if user has typed something
    if (issueSearch || !initialIssueId) {
      const timeoutId = setTimeout(() => {
        fetchIssues(issueSearch)
      }, 300)

      return () => clearTimeout(timeoutId)
    }
  }, [issueSearch, isOpen, initialIssueId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.hours || parseFloat(formData.hours) <= 0) {
      toast({ title: 'Validation Error', description: 'Please enter valid hours', variant: 'destructive' })
      return
    }

    if (!formData.issue_id) {
      toast({ title: 'Validation Error', description: 'Please select an issue', variant: 'destructive' })
      return
    }

    try {
      setSubmitting(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      // Stop timer if active
      if (isTimerActive) {
        stopTimer()
      }

      const timeEntryData = {
        time_entry: {
          issue_id: parseInt(formData.issue_id),
          hours: parseFloat(formData.hours),
          activity_id: parseInt(formData.activity_id),
          comments: formData.comments,
          spent_on: formData.spent_on
        }
      }

      // Determine if we're editing or creating
      const isEditing = !!timeEntryId
      const url = isEditing ? `/api/time_entries/${timeEntryId}` : '/api/time_entries'
      const method = isEditing ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(timeEntryData)
      })

      if (response.ok) {
        // Reset form
        setFormData({
          hours: '',
          activity_id: defaultActivityId,
          issue_id: '',
          comments: '',
          spent_on: new Date().toISOString().split('T')[0]
        })

        // Dispatch event to notify other components
        if (isEditing) {
          window.dispatchEvent(new CustomEvent('timeEntryUpdated', {
            detail: { timeEntryId, issueId: parseInt(formData.issue_id) }
          }))
        } else {
          window.dispatchEvent(new CustomEvent('timeLogged', {
            detail: { issueId: parseInt(formData.issue_id) }
          }))
        }

        onSuccess?.()
        onClose()
      } else {
        toast({ title: 'Error', description: isEditing ? 'Failed to update time entry' : 'Failed to log time', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save time entry. Please try again.', variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getActivityName = (id: string) => {
    const activity = activities.find(a => a.id.toString() === id)
    return activity?.name || 'Select activity'
  }

  const getIssueName = (id: string) => {
    const issue = issues.find(i => i.id.toString() === id)
    return issue ? `#${issue.id}: ${issue.subject}` : 'Select issue'
  }

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={timeEntryId ? 'Edit Time Entry' : 'Log Time'}
      subtitle={selectedIssue ? `#${selectedIssue.id}: ${selectedIssue.subject}` : undefined}
      icon={<Clock className="h-5 w-5" />}
      width="md"
      closeOnBackdropClick={!submitting}
      footer={
        <div className="flex gap-3">
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || loading}
            className="flex-1"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Logging...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {timeEntryId ? 'Update Time Entry' : isTimerActive ? 'Stop Timer & Log Time' : 'Log Time'}
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Active Timer Display */}
        {isTimerActive && (
          <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-red-700 dark:text-red-400">
                  Timer Running
                </span>
              </div>
              <div className="text-2xl font-mono font-bold text-red-600 dark:text-red-400">
                {formatTime(elapsedSeconds)}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Time will be automatically logged when you submit
            </p>
          </div>
        )}

        {/* Form Content */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Selected Issue Card Display */}
            {selectedIssue && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-foreground">
                    Issue <span className="text-red-500">*</span>
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedIssue(null)
                      setFormData({ ...formData, issue_id: '' })
                      // Fetch general issues list when user wants to change issue
                      fetchIssues()
                    }}
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Change Issue
                  </Button>
                </div>
                <IssueCard
                  issue={selectedIssue}
                  showTimeTracker={false}
                  compact={true}
                  showProjectName={true}
                />
              </div>
            )}

            {/* Issue Selection with Search - Only show if no issue is selected */}
            {!selectedIssue && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Issue <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search issues by ID or name..."
                    value={issueSearch}
                    onChange={(e) => setIssueSearch(e.target.value)}
                    className="mb-2"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {issues.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {issueSearch ? 'No issues found. Try a different search.' : 'Loading issues...'}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {issues.map(issue => (
                        <button
                          key={issue.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, issue_id: issue.id.toString() })
                            setSelectedIssue(issue)
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors ${
                            issue.id.toString() === formData.issue_id ? 'bg-muted font-medium' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-xs text-muted-foreground shrink-0">#{issue.number ?? issue.id}</span>
                            <span className="text-sm truncate flex-1">{issue.subject}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {issue.project.name}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hours */}
            <div>
              <label htmlFor="hours" className="block text-sm font-medium text-foreground mb-2">
                Hours <span className="text-red-500">*</span>
              </label>
              <Input
                id="hours"
                type="number"
                step="0.25"
                min="0.01"
                placeholder="e.g., 2.5"
                value={formData.hours}
                onChange={(e) => {
                  setFormData({ ...formData, hours: e.target.value })
                  // Mark as manually edited when user changes the value
                  setIsManuallyEditingHours(true)
                }}
                onFocus={() => setIsManuallyEditingHours(true)}
                required
                className="text-lg"
                disabled={isTimerActive && !activeTimer?.isPaused && !isManuallyEditingHours}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {isTimerActive && !activeTimer?.isPaused && !isManuallyEditingHours
                  ? 'Hours are auto-updating from active timer. Click to edit manually.'
                  : 'Enter time in hours (e.g., 1.5 for 1 hour 30 minutes)'}
              </p>
            </div>

            {/* Activity */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Activity <span className="text-red-500">*</span>
              </label>
              <CustomDropdown
                label="Activity"
                currentValue={getActivityName(formData.activity_id)}
                options={activities.map(activity => ({
                  id: activity.id,
                  name: activity.name,
                  isSelected: activity.id.toString() === formData.activity_id
                }))}
                onSelect={(id) => setFormData({ ...formData, activity_id: id?.toString() || defaultActivityId })}
                placeholder="Select an activity"
              />
            </div>

            {/* Date */}
            <div>
              <label htmlFor="spent_on" className="block text-sm font-medium text-foreground mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="spent_on"
                  type="date"
                  value={formData.spent_on}
                  onChange={(e) => setFormData({ ...formData, spent_on: e.target.value })}
                  required
                  className="pl-10"
                />
              </div>
            </div>

            {/* Comments */}
            <div>
              <label htmlFor="comments" className="block text-sm font-medium text-foreground mb-2">
                Comments
              </label>
              <textarea
                id="comments"
                rows={6}
                placeholder="Describe what you worked on..."
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional description of work done
              </p>
            </div>
          </form>
      </div>
    </GlobalSidebar>
  )
}
